import React from 'react'
import ReactDOM from 'react-dom/client'
import { ChakraProvider, extendTheme } from '@chakra-ui/react'
import { BrowserRouter } from 'react-router-dom'
import App from './App'

const theme = extendTheme({
  config: {
    initialColorMode: 'dark',
    useSystemColorMode: false,
  },
  fonts: {
    heading: "'Inter', sans-serif",
    body: "'Inter', sans-serif",
  },
  colors: {
    brand: {
      50: '#FFF9E5',
      100: '#FFEEB8',
      200: '#FFE38A',
      300: '#FFD85C',
      400: '#FFCD2E',
      500: '#ECC94B',
      600: '#D4A017',
      700: '#A67C00',
      800: '#785800',
      900: '#4A3500',
    },
    gray: {
      50: '#F7FAFC',
      100: '#EDF2F7',
      200: '#E2E8F0',
      300: '#CBD5E0',
      400: '#A0AEC0',
      500: '#718096',
      600: '#4A5568',
      700: '#2D3748',
      800: '#1A202C',
      900: '#171923',
    },
  },
  semanticTokens: {
    colors: {
      'bg.page': { default: 'gray.50', _dark: 'gray.900' },
      'bg.surface': { default: 'white', _dark: 'gray.800' },
      'bg.subtle': { default: 'gray.100', _dark: 'gray.700' },
      'bg.input': { default: 'white', _dark: 'gray.800' },
      'bg.hover': { default: 'gray.100', _dark: 'gray.700' },
      'bg.active': { default: 'gray.200', _dark: 'gray.600' },
      'text.primary': { default: 'gray.800', _dark: 'white' },
      'text.secondary': { default: 'gray.600', _dark: 'gray.300' },
      'text.muted': { default: 'gray.500', _dark: 'gray.400' },
      'text.inverted': { default: 'white', _dark: 'gray.900' },
      'border.default': { default: 'gray.200', _dark: 'gray.700' },
      'border.subtle': { default: 'gray.100', _dark: 'gray.600' },
      'scrollbar.track': { default: 'gray.100', _dark: 'gray.800' },
      'scrollbar.thumb': { default: 'gray.300', _dark: 'gray.600' },
      'scrollbar.thumbHover': { default: 'gray.400', _dark: 'gray.500' },
    },
  },
  components: {
    Button: {
      defaultProps: {
        colorScheme: 'yellow',
      },
      variants: {
        primary: {
          bg: 'yellow.500',
          color: 'gray.900',
          _hover: {
            bg: 'yellow.400',
          },
        },
        outline: (props: any) => ({
          borderColor: props.colorMode === 'dark' ? 'gray.600' : 'gray.300',
          color: props.colorMode === 'dark' ? 'gray.300' : 'gray.600',
          _hover: {
            bg: props.colorMode === 'dark' ? 'gray.700' : 'gray.100',
          },
        }),
      },
    },
    Input: {
      defaultProps: {
        variant: 'outline',
      },
      variants: {
        outline: (props: any) => ({
          field: {
            bg: props.colorMode === 'dark' ? 'gray.800' : 'white',
            borderColor: props.colorMode === 'dark' ? 'gray.600' : 'gray.300',
            _hover: {
              borderColor: props.colorMode === 'dark' ? 'gray.500' : 'gray.400',
            },
            _focus: {
              borderColor: 'yellow.500',
              boxShadow: '0 0 0 1px var(--chakra-colors-yellow-500)',
            },
          },
        }),
      },
    },
    Textarea: {
      variants: {
        outline: (props: any) => ({
          bg: props.colorMode === 'dark' ? 'gray.800' : 'white',
          borderColor: props.colorMode === 'dark' ? 'gray.600' : 'gray.300',
          _hover: {
            borderColor: props.colorMode === 'dark' ? 'gray.500' : 'gray.400',
          },
          _focus: {
            borderColor: 'yellow.500',
            boxShadow: '0 0 0 1px var(--chakra-colors-yellow-500)',
          },
        }),
      },
    },
    Select: {
      variants: {
        outline: (props: any) => ({
          field: {
            bg: props.colorMode === 'dark' ? 'gray.800' : 'white',
            borderColor: props.colorMode === 'dark' ? 'gray.600' : 'gray.300',
            _hover: {
              borderColor: props.colorMode === 'dark' ? 'gray.500' : 'gray.400',
            },
          },
        }),
      },
    },
    Modal: {
      baseStyle: (props: any) => ({
        dialog: {
          bg: props.colorMode === 'dark' ? 'gray.800' : 'white',
        },
      }),
    },
    Menu: {
      baseStyle: (props: any) => ({
        list: {
          bg: props.colorMode === 'dark' ? 'gray.800' : 'white',
          borderColor: props.colorMode === 'dark' ? 'gray.700' : 'gray.200',
        },
        item: {
          bg: props.colorMode === 'dark' ? 'gray.800' : 'white',
          _hover: {
            bg: props.colorMode === 'dark' ? 'gray.700' : 'gray.100',
          },
        },
      }),
    },
    Table: {
      variants: {
        simple: (props: any) => ({
          th: {
            borderColor: props.colorMode === 'dark' ? 'gray.700' : 'gray.200',
          },
          td: {
            borderColor: props.colorMode === 'dark' ? 'gray.700' : 'gray.200',
          },
        }),
      },
    },
  },
  styles: {
    global: (props: any) => ({
      // Altura real da tela no celular: 100dvh acompanha a barra do navegador e
      // o teclado (100vh não), e o app ocupa a tela inteira como um app nativo.
      'html, body, #root': {
        height: '100%',
        overscrollBehaviorY: 'none',
      },
      body: {
        bg: props.colorMode === 'dark' ? 'gray.900' : 'gray.50',
        color: props.colorMode === 'dark' ? 'gray.100' : 'gray.800',
        WebkitTapHighlightColor: 'transparent',
        WebkitTextSizeAdjust: '100%',
      },
      // iOS dá zoom ao focar campo com fonte < 16px: no celular os campos usam 16px.
      '@media (max-width: 61.99em)': {
        'input, textarea, select': {
          fontSize: '16px !important',
        },
      },
      '*::-webkit-scrollbar': {
        width: '8px',
        height: '8px',
      },
      '*::-webkit-scrollbar-track': {
        bg: props.colorMode === 'dark' ? 'gray.800' : 'gray.100',
      },
      '*::-webkit-scrollbar-thumb': {
        bg: props.colorMode === 'dark' ? 'gray.600' : 'gray.300',
        borderRadius: '4px',
      },
      '*::-webkit-scrollbar-thumb:hover': {
        bg: props.colorMode === 'dark' ? 'gray.500' : 'gray.400',
      },
    }),
  },
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ChakraProvider theme={theme}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </ChakraProvider>
  </React.StrictMode>,
)
